import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import fileSaver from 'file-saver';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const BulkImportModal = ({ tenantId, userId, categories, onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Generate Excel template with product schema
  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'SKU*': 'PROD-001',
        'Product Name*': 'Sample Product',
        'Description': 'Product description here',
        'Category': 'Supplements',
        'Price*': 29.99,
        'Cost Price': 15.00,
        'Stock Quantity': 100,
        'Low Stock Threshold': 10,
        'Barcode': '1234567890123',
        'Track Inventory': 'YES',
        'Status': 'active',
        'Image URL': 'https://example.com/image.jpg'
      },
      {
        'SKU*': 'PROD-002',
        'Product Name*': 'Another Product',
        'Description': 'Another description',
        'Category': 'Apparel',
        'Price*': 49.99,
        'Cost Price': 25.00,
        'Stock Quantity': 50,
        'Low Stock Threshold': 5,
        'Barcode': '',
        'Track Inventory': 'YES',
        'Status': 'active',
        'Image URL': ''
      }
    ];

    // Create workbook and worksheet
    const wb = XLSX?.utils?.book_new();
    const ws = XLSX?.utils?.json_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // SKU
      { wch: 25 }, // Product Name
      { wch: 40 }, // Description
      { wch: 15 }, // Category
      { wch: 10 }, // Price
      { wch: 12 }, // Cost Price
      { wch: 15 }, // Stock Quantity
      { wch: 20 }, // Low Stock Threshold
      { wch: 18 }, // Barcode
      { wch: 18 }, // Track Inventory
      { wch: 12 }, // Status
      { wch: 30 }  // Image URL
    ];

    // Add worksheet to workbook
    XLSX?.utils?.book_append_sheet(wb, ws, 'Products');

    // Generate Excel file
    const excelBuffer = XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Download file
    fileSaver.saveAs(blob, `product-import-template-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      validateFile(selectedFile);
    }
  };

  // Validate file before processing
  const validateFile = (selectedFile) => {
    const errors = [];

    // Check file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes?.includes(selectedFile?.type)) {
      errors?.push('Invalid file type. Please upload an Excel file (.xlsx or .xls)');
    }

    // Check file size (max 5MB)
    if (selectedFile?.size > 5 * 1024 * 1024) {
      errors?.push('File size exceeds 5MB limit');
    }

    if (errors?.length > 0) {
      setValidationErrors(errors);
      setFile(null);
    } else {
      setValidationErrors([]);
      setFile(selectedFile);
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      validateFile(e?.dataTransfer?.files?.[0]);
    }
  };

  // Parse Excel file and validate data
  const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e?.target?.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  // Validate product data
  const validateProductData = (products) => {
    const errors = [];
    const validStatuses = ['active', 'inactive', 'out_of_stock', 'discontinued'];
    
    products?.forEach((product, index) => {
      const rowNum = index + 2; // +2 for header row and 0-based index
      
      // Required fields
      if (!product?.['SKU*']) {
        errors?.push(`Row ${rowNum}: SKU is required`);
      }
      if (!product?.['Product Name*']) {
        errors?.push(`Row ${rowNum}: Product Name is required`);
      }
      if (!product?.['Price*'] || isNaN(parseFloat(product?.['Price*']))) {
        errors?.push(`Row ${rowNum}: Valid Price is required`);
      }
      
      // Validate status
      if (product?.['Status'] && !validStatuses?.includes(product?.['Status']?.toLowerCase())) {
        errors?.push(`Row ${rowNum}: Invalid status. Must be one of: ${validStatuses?.join(', ')}`);
      }
      
      // Validate numeric fields
      if (product?.['Cost Price'] && isNaN(parseFloat(product?.['Cost Price']))) {
        errors?.push(`Row ${rowNum}: Cost Price must be a number`);
      }
      if (product?.['Stock Quantity'] && isNaN(parseInt(product?.['Stock Quantity']))) {
        errors?.push(`Row ${rowNum}: Stock Quantity must be a number`);
      }
      if (product?.['Low Stock Threshold'] && isNaN(parseInt(product?.['Low Stock Threshold']))) {
        errors?.push(`Row ${rowNum}: Low Stock Threshold must be a number`);
      }
    });
    
    return errors;
  };

  // Handle import process
  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    setValidationErrors([]);
    setImportResults(null);
    
    try {
      // Parse Excel file
      const products = await parseExcelFile(file);
      
      if (!products || products?.length === 0) {
        setValidationErrors(['No products found in the file']);
        setImporting(false);
        return;
      }
      
      // Validate data
      const errors = validateProductData(products);
      if (errors?.length > 0) {
        setValidationErrors(errors);
        setImporting(false);
        return;
      }
      
      // Find category IDs by name
      const categoryMap = {};
      categories?.forEach(cat => {
        categoryMap[cat?.name?.toLowerCase()] = cat?.id;
      });
      
      // Transform data for database
      const productsToInsert = products?.map(product => {
        const categoryName = product?.['Category']?.toLowerCase();
        const categoryId = categoryName ? categoryMap?.[categoryName] : null;
        
        return {
          tenant_id: tenantId,
          sku: product?.['SKU*']?.toString()?.trim(),
          name: product?.['Product Name*']?.toString()?.trim(),
          description: product?.['Description']?.toString()?.trim() || null,
          category_id: categoryId,
          price: parseFloat(product?.['Price*']) || 0,
          cost: parseFloat(product?.['Cost Price']) || 0,
          stock_quantity: parseInt(product?.['Stock Quantity']) || 0,
          low_stock_threshold: parseInt(product?.['Low Stock Threshold']) || 10,
          barcode: product?.['Barcode']?.toString()?.trim() || null,
          track_inventory: product?.['Track Inventory']?.toString()?.toUpperCase() === 'YES',
          is_active: product?.['Status']?.toLowerCase() === 'active' || !product?.['Status'],
          image_url: product?.['Image URL']?.toString()?.trim() || null,
          created_by: userId
        };
      });
      
      // Insert products in batches
      const batchSize = 50;
      let successCount = 0;
      let failCount = 0;
      const failedProducts = [];
      
      for (let i = 0; i < productsToInsert?.length; i += batchSize) {
        const batch = productsToInsert?.slice(i, i + batchSize);
        
        const { data, error } = await supabase
          ?.from('pos_products')
          ?.insert(batch)
          ?.select();
        
        if (error) {
          console.error('Batch insert error:', error);
          failCount += batch?.length;
          failedProducts?.push(...batch?.map(p => p?.sku));
        } else {
          successCount += data?.length || 0;
        }
      }
      
      setImportResults({
        total: productsToInsert?.length,
        success: successCount,
        failed: failCount,
        failedProducts
      });
      
      if (successCount > 0) {
        onImportComplete?.();
      }
      
    } catch (error) {
      console.error('Import error:', error);
      setValidationErrors([`Import failed: ${error?.message}`]);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto pt-24">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground">Bulk Product Import</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Import multiple products using Excel spreadsheet
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Download Template */}
          <div className="mb-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary">1</span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Download Template
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Download the Excel template with sample data and required columns
                </p>
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                >
                  <Icon name="Download" size={18} className="mr-2" />
                  Download Excel Template
                </Button>
              </div>
            </div>
          </div>

          {/* Step 2: Upload File */}
          <div className="mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary">2</span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Upload Completed File
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Fill in the template and upload it here
                </p>
                
                {/* Drag and Drop Area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
                    dragActive
                      ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <Icon
                    name="Upload"
                    size={48}
                    className="text-muted-foreground mx-auto mb-4"
                  />
                  <p className="text-foreground font-medium mb-2">
                    Drag and drop your Excel file here
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">or</p>
                  <label className="inline-block">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <span className="px-4 py-2 bg-primary text-white rounded-lg cursor-pointer hover:bg-primary/90 transition-smooth inline-block">
                      Browse Files
                    </span>
                  </label>
                  <p className="text-xs text-muted-foreground mt-3">
                    Supported formats: .xlsx, .xls (Max 5MB)
                  </p>
                </div>

                {/* Selected File */}
                {file && (
                  <div className="mt-4 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="FileSpreadsheet" size={24} className="text-success" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{file?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file?.size / 1024)?.toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {validationErrors?.length > 0 && (
            <div className="mb-6 p-4 bg-danger/10 border border-danger/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-danger flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-danger mb-2">
                    Validation Errors ({validationErrors?.length})
                  </h4>
                  <ul className="text-sm text-danger space-y-1 max-h-40 overflow-y-auto">
                    {validationErrors?.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Import Results */}
          {importResults && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-success mb-2">
                    Import Complete
                  </h4>
                  <div className="text-sm text-foreground space-y-1">
                    <p>Total Products: {importResults?.total}</p>
                    <p className="text-success">Successfully Imported: {importResults?.success}</p>
                    {importResults?.failed > 0 && (
                      <p className="text-danger">Failed: {importResults?.failed}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Template Instructions */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Info" size={16} className="text-primary" />
              Template Instructions
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Fields marked with * are required</li>
              <li>• SKU must be unique for each product</li>
              <li>• Category must match existing category names (case-insensitive)</li>
              <li>• Status options: active, inactive, out_of_stock, discontinued</li>
              <li>• Track Inventory: YES or NO</li>
              <li>• Price and Cost Price must be numbers</li>
              <li>• Stock Quantity and Low Stock Threshold must be whole numbers</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!file || importing || validationErrors?.length > 0}
          >
            {importing ? (
              <>
                <Icon name="Loader2" size={18} className="mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Icon name="Upload" size={18} className="mr-2" />
                Import Products
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;